// LeaveMatchDialog — in-game leave confirmation for active online matches.
//
// Responsibilities:
//   - Shows a floating "Leave Match" button anchored to the top-right corner
//     of the screen while an online match is active.
//   - Clicking the button (or calling open()) opens a modal overlay that:
//       • Warns "The online match continues while this dialog is open."
//       • Warns "The match is still running. Leaving may count as a loss/forfeit."
//       • Offers two actions: Cancel (closes the dialog) and Leave Match
//         (calls the provided onLeave callback).
//   - Registers a beforeunload handler while attached so that browser
//     refresh, F5, closing the tab, or navigating away triggers the
//     browser's native "Leave site?" confirmation. The handler is removed
//     on detach() so the warning never fires outside active matches.
//   - Pure presentation (Rule 1). Holds no gameplay state.
//   - The caller (ClientApp) is responsible for calling attach/detach at
//     the right match lifecycle transitions and wiring onLeave → endMatch().
//
// Disconnect/reconnect fallback note:
//   When a player leaves or refreshes during an active online match, the
//   server emits `opponent_disconnected` to the remaining player and starts a
//   reconnect countdown (shown via ReconnectBanner). If the disconnected
//   client does not rejoin within the server-side timeout the server removes
//   them and awards the remaining player the win. No client-side match-state
//   persistence is needed; the server is the sole authority (Rule 1).

const TRIGGER_ID = 'leave-match-trigger';
const OVERLAY_ID = 'leave-match-overlay';
const STYLE_ID = 'leave-match-dialog-styles';

export class LeaveMatchDialog {
  private triggerEl: HTMLButtonElement | null = null;
  private overlayEl: HTMLDivElement | null = null;
  private onLeave: (() => void) | null = null;
  private beforeunloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;

  constructor() {
    injectStyles();
  }

  // Attach the leave callback, show the trigger button, and register the
  // beforeunload guard. Call when an online match starts.
  attach(onLeave: () => void): void {
    this.onLeave = onLeave;
    this.ensureTrigger();
    this.ensureOverlay();
    if (this.triggerEl !== null) this.triggerEl.style.display = '';
    this.attachBeforeunloadGuard();
  }

  // Hide the trigger button, close any open overlay, and remove the
  // beforeunload guard. Call when the match ends or the player returns
  // to the main menu.
  detach(): void {
    this.close();
    if (this.triggerEl !== null) this.triggerEl.style.display = 'none';
    this.onLeave = null;
    this.removeBeforeunloadGuard();
  }

  // Programmatically open the confirmation overlay (same as clicking the
  // trigger). Exposed so ESC-key handlers or other callers can open it.
  open(): void {
    if (this.overlayEl === null) return;
    this.overlayEl.style.display = 'flex';
  }

  // Close the overlay without leaving the match.
  close(): void {
    if (this.overlayEl !== null) this.overlayEl.style.display = 'none';
  }

  dispose(): void {
    this.detach();
    this.triggerEl?.remove();
    this.triggerEl = null;
    this.overlayEl?.remove();
    this.overlayEl = null;
  }

  // Register a beforeunload handler so we can clean up local presentation
  // state (audio, dialogs) before the page unloads. We deliberately do NOT
  // call preventDefault / set returnValue — the CrazyGames iframe samples
  // a native "Leave site?" confirm dialog as an unclean exit and rolls
  // it into the crash-rate KPI. The in-game LeaveMatchDialog modal is
  // the supported intent-confirmation surface; the server-side reconnect
  // window (90 s) already covers accidental closes by giving the player
  // time to come back without forfeiting the match.
  private attachBeforeunloadGuard(): void {
    if (typeof window === 'undefined' || this.beforeunloadHandler !== null) return;
    this.beforeunloadHandler = (): void => {
      // Intentionally empty — kept as an attached listener so future
      // teardown hooks (e.g. flushing analytics) have a wire to ride
      // on without re-introducing the native confirm dialog.
    };
    window.addEventListener('beforeunload', this.beforeunloadHandler);
  }

  private removeBeforeunloadGuard(): void {
    if (typeof window === 'undefined' || this.beforeunloadHandler === null) return;
    window.removeEventListener('beforeunload', this.beforeunloadHandler);
    this.beforeunloadHandler = null;
  }

  private ensureTrigger(): void {
    if (this.triggerEl !== null) return;
    const existing = document.getElementById(TRIGGER_ID);
    if (existing instanceof HTMLButtonElement) {
      this.triggerEl = existing;
      return;
    }
    const btn = document.createElement('button');
    btn.id = TRIGGER_ID;
    btn.type = 'button';
    btn.className = 'leave-match-trigger';
    btn.textContent = 'Leave Match';
    btn.addEventListener('click', () => this.open());
    document.body.appendChild(btn);
    this.triggerEl = btn;
  }

  private ensureOverlay(): void {
    if (this.overlayEl !== null) return;
    const existing = document.getElementById(OVERLAY_ID);
    if (existing instanceof HTMLDivElement) {
      this.overlayEl = existing;
      return;
    }
    const overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.className = 'leave-match-overlay';
    overlay.style.display = 'none';

    const card = document.createElement('div');
    card.className = 'leave-match-card';

    const title = document.createElement('h2');
    title.className = 'leave-match-title';
    title.textContent = 'LEAVE MATCH';
    card.appendChild(title);

    const onlineNote = document.createElement('p');
    onlineNote.className = 'leave-match-online-note';
    onlineNote.textContent = 'The online match continues while this dialog is open.';
    card.appendChild(onlineNote);

    const warning = document.createElement('p');
    warning.className = 'leave-match-warning';
    warning.textContent =
      'The match is still running. Leaving may count as a loss / forfeit.';
    card.appendChild(warning);

    const actions = document.createElement('div');
    actions.className = 'leave-match-actions';

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'leave-match-btn leave-match-btn--cancel';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', () => this.close());
    actions.appendChild(cancelBtn);

    const leaveBtn = document.createElement('button');
    leaveBtn.type = 'button';
    leaveBtn.className = 'leave-match-btn leave-match-btn--leave';
    leaveBtn.textContent = 'Leave Match';
    leaveBtn.addEventListener('click', () => {
      this.close();
      this.onLeave?.();
    });
    actions.appendChild(leaveBtn);

    card.appendChild(actions);
    overlay.appendChild(card);

    // Clicking the backdrop (outside the card) cancels.
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.close();
    });

    document.body.appendChild(overlay);
    this.overlayEl = overlay;
  }
}

function injectStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
/* V2 Tempo polish: trigger pill matches the HUD chrome — hex clip-path,
 * cyan rim, same gradient as .hud__enemy in the opposite corner. */
#${TRIGGER_ID} {
  position: fixed;
  top: 12px;
  right: 220px; /* clears the enemy resources panel (top-right) */
  z-index: 40;
  display: none;
  padding: 7px 14px;
  background: linear-gradient(180deg, rgba(20,28,38,0.85), rgba(10,14,20,0.92));
  border: 1px solid rgba(174, 240, 255, 0.30);
  border-radius: 0;
  clip-path: polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px);
  color: #6fdcff;
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 150ms, box-shadow 150ms;
  box-shadow: 0 0 0 1px rgba(106,216,255,0.06), 0 6px 18px rgba(0,0,0,0.5);
}
#${TRIGGER_ID}:hover {
  color: #aef0ff;
  box-shadow: 0 0 16px rgba(30,167,255,0.30);
}
/* Overlay — full-screen backdrop */
#${OVERLAY_ID} {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: none;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.72);
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.leave-match-card {
  position: relative;
  background:
    radial-gradient(ellipse 100% 100% at 0% 0%, rgba(30, 167, 255, 0.14), transparent 60%),
    radial-gradient(ellipse 100% 100% at 100% 100%, rgba(255, 122, 90, 0.10), transparent 60%),
    linear-gradient(180deg, rgba(10, 19, 34, 0.96), rgba(5, 9, 17, 0.98));
  border: 1px solid rgba(174, 240, 255, 0.32);
  border-radius: 0;
  clip-path: polygon(14px 0, 100% 0, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%, 0 14px);
  padding: 28px 32px 24px;
  max-width: 440px;
  width: 90%;
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.06) inset,
    0 0 36px rgba(30,167,255,0.12) inset,
    0 12px 40px rgba(0,0,0,0.7);
  text-align: center;
}
.leave-match-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: linear-gradient(180deg, rgba(174,240,255,0.04) 1px, transparent 1px);
  background-size: 100% 20px;
  opacity: 0.40;
  pointer-events: none;
}
.leave-match-card > * { position: relative; z-index: 1; }
.leave-match-title {
  color: #aef0ff;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  margin: 0 0 16px;
  text-shadow: 0 0 12px rgba(110,220,255,0.45);
}
.leave-match-online-note {
  color: #6fdcff;
  font-size: 11px;
  letter-spacing: 0.10em;
  margin: 0 0 12px;
  text-transform: uppercase;
}
.leave-match-warning {
  color: #ffe680;
  font-size: 13px;
  letter-spacing: 0.06em;
  margin: 0 0 24px;
  text-shadow: 0 0 8px rgba(255,230,128,0.30);
}
.leave-match-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}
.leave-match-btn {
  padding: 11px 24px;
  border-radius: 0;
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.20em;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 150ms, box-shadow 150ms, transform 80ms;
  clip-path: polygon(10px 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0 50%);
  min-height: 40px;
}
.leave-match-btn--cancel {
  background: linear-gradient(180deg, rgba(20,28,38,0.85), rgba(10,14,20,0.92));
  border: 1px solid rgba(174, 240, 255, 0.28);
  color: #cfd6dd;
}
.leave-match-btn--cancel:hover {
  color: #aef0ff;
  box-shadow: 0 0 14px rgba(30,167,255,0.30);
}
.leave-match-btn--leave {
  background: linear-gradient(180deg, rgba(255, 42, 42, 0.15), rgba(60,18,18,0.85));
  border: 1px solid rgba(255, 122, 90, 0.55);
  color: #ff7a5a;
  text-shadow: 0 0 8px rgba(255,122,90,0.45);
}
.leave-match-btn--leave:hover {
  color: #ffd9b0;
  box-shadow: 0 0 14px rgba(255, 122, 90, 0.45);
}
.leave-match-btn:active { transform: scale(0.98); }
@media (max-width: 820px), (pointer: coarse) {
  #${TRIGGER_ID} { font-size: 10px; padding: 4px 10px; top: 8px; right: 8px; }
  .leave-match-title { font-size: 17px; }
  .leave-match-warning { font-size: 13px; }
  .leave-match-btn { font-size: 11px; padding: 8px 18px; }
}
`;
  document.head.appendChild(style);
}

